import { Tabs, TabsList, TabsTrigger } from "@aurumiq/ui/components/tabs";
import { CopyButton } from "@aurumiq/ui/composite/copy-button";
import { StatusTag, statusVariant } from "@aurumiq/ui/composite/status-tag";
import { NavLink, Outlet, useParams } from "react-router";
import { PageHeader } from "@/app/page-header";
import { BillingTab } from "@/features/orgs/billing-tab";
import { DevicesTab } from "@/features/orgs/devices-tab";
import { LicensesTab } from "@/features/orgs/licenses-tab";
import { MetricsTab } from "@/features/orgs/metrics-tab";
import { OverviewTab } from "@/features/orgs/overview-tab";
import { useOrg } from "@/features/orgs/queries";
import { TelemetryTab } from "@/features/orgs/telemetry-tab";
import { UsersTab } from "@/features/orgs/users-tab";
import { NotFoundPage } from "./errors";

const TABS = [
  ["overview", "Overview"],
  ["users", "Users"],
  ["licenses", "Licenses"],
  ["devices", "Devices"],
  ["billing", "Billing"],
  ["metrics", "Metrics"],
  ["telemetry", "Telemetry"],
] as const;

type TabKey = (typeof TABS)[number][0];

export function OrgDetailLayout() {
  const { id = "", tab = "overview" } = useParams();
  const org = useOrg(id);
  const o = org.data;
  return (
    <>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-2">
            {o?.name ?? (org.isPending ? "Loading…" : `Organisation ${id}`)}
            {o ? (
              <StatusTag variant={statusVariant(o.status)}>{o.status.toUpperCase()}</StatusTag>
            ) : null}
          </span>
        }
        description={
          o ? (
            <span className="inline-flex items-center gap-1.5">
              <span className="font-mono">{o.code}</span>
              <CopyButton value={o.code} variant="ghost" />
              <span aria-hidden="true">·</span>
              {o.plan_code} · {o.seats_used}/{o.seats} seats
            </span>
          ) : (
            "Tenant detail"
          )
        }
      />
      <Tabs value={tab} className="mb-4">
        <TabsList className="h-auto flex-wrap">
          {TABS.map(([key, label]) => (
            <TabsTrigger key={key} value={key} asChild>
              <NavLink to={key === "overview" ? `/orgs/${id}` : `/orgs/${id}/${key}`} end>
                {label}
              </NavLink>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <Outlet />
    </>
  );
}

export function OrgTab() {
  const { id = "", tab = "overview" } = useParams();
  switch (tab as TabKey) {
    case "overview":
      return <OverviewTab orgId={id} />;
    case "users":
      return <UsersTab orgId={id} />;
    case "licenses":
      return <LicensesTab orgId={id} />;
    case "devices":
      return <DevicesTab orgId={id} />;
    case "billing":
      return <BillingTab orgId={id} />;
    case "metrics":
      return <MetricsTab orgId={id} />;
    case "telemetry":
      return <TelemetryTab orgId={id} />;
    default:
      return <NotFoundPage />;
  }
}
